/**
 * Historical Analytics Dashboard v2 (Jun 2026) — premium executive view.
 * Single shared filter set (MTD default) drives every KPI, chart, table,
 * insight and export. Layout per approved mock (user choices 1a-5a).
 */
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../ui/button';
import { Skeleton } from '../ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { TeamMemberSelect } from './TeamMemberSelect';
import { Tooltip as UITooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip';
import { toast } from 'sonner';
import { AnFilterBar, AnFilterField, AnDateRange, AnResetButton, AnRunButton, AnKpiCard, AnDelta, AnChartCard } from './ui/AnalyticsUi';
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, LabelList, Legend,
  XAxis, YAxis, CartesianGrid, Tooltip as RTooltip, ResponsiveContainer,
} from 'recharts';
import {
  TrendingUp, TrendingDown, Minus, Download, Play, Loader2, Users, Inbox,
  Clock, CheckCircle2, Gauge, FileCheck2, Lightbulb, AlertTriangle, Layers,
  ArrowUpRight, Info, Hourglass, Flame, Timer, CalendarClock,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const SLA_COLORS = { within: '#10B981', near: '#F59E0B', over: '#EF4444' };

const mtdStart = () => {
  const n = new Date();
  return `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, '0')}-01`;
};
const today = () => new Date().toISOString().slice(0, 10);

export const buildFilterParams = (f) => {
  const p = new URLSearchParams({ start_date: f.start, end_date: f.end });
  if (f.orderType && f.orderType !== 'all') p.set('order_type', f.orderType);
  if (f.product && f.product !== 'all') p.set('product', f.product);
  if (f.client && f.client !== 'all') p.set('client', f.client);
  if (f.userId && f.userId !== 'all') p.set('user_id', f.userId);
  return p.toString();
};

const DeltaChip = ({ pct, invert = false, suffix = '%' }) => {
  if (pct === null || pct === undefined) return <span className="inline-flex items-center gap-1 text-xs font-medium text-slate-400"><Minus className="w-3 h-3" />—</span>;
  const good = invert ? pct <= 0 : pct >= 0;
  const Icon = pct === 0 ? Minus : pct > 0 ? TrendingUp : TrendingDown;
  const color = pct === 0 ? 'text-slate-400' : good ? 'text-emerald-600' : 'text-red-500';
  return (
    <span className={`inline-flex items-center gap-1 text-xs font-semibold ${color}`}>
      <Icon className="w-3.5 h-3.5" />{Math.abs(pct)}{suffix}
    </span>
  );
};

const Sparkline = ({ data }) => (
  <ResponsiveContainer width={90} height={26}>
    <LineChart data={(data || []).map((v, i) => ({ i, v }))}>
      <Line type="monotone" dataKey="v" stroke="#2563EB" strokeWidth={1.5} dot={false} />
    </LineChart>
  </ResponsiveContainer>
);

const SectionCard = (props) => <AnChartCard {...props} />;

const EmptyState = ({ label = 'No data for this period', height = 220 }) => (
  <div className="flex items-center justify-center text-slate-400 text-sm" style={{ height }}>{label}</div>
);

const KPI_CONFIG = [
  { key: 'orders_received', label: 'Orders Received', icon: Inbox, tint: 'bg-sky-50 text-sky-600' },
  { key: 'files_completed', label: 'Files Completed', icon: FileCheck2, tint: 'bg-blue-50 text-blue-600' },
  { key: 'pending_backlog', label: 'Pending Backlog', icon: Layers, tint: 'bg-orange-50 text-orange-600', invert: true },
  { key: 'avg_completion_time', label: 'Avg Completion Time', icon: Clock, tint: 'bg-violet-50 text-violet-600', invert: true, useLabel: true },
  { key: 'sla_compliance', label: 'SLA Compliance', icon: CheckCircle2, tint: 'bg-emerald-50 text-emerald-600', suffix: '%', pts: true },
  { key: 'productivity_change', label: 'Productivity vs Prev', icon: Gauge, tint: 'bg-cyan-50 text-cyan-600' },
  { key: 'active_processors', label: 'Active Processors', icon: Users, tint: 'bg-amber-50 text-amber-600' },
];

const KPI_TONE = { orders_received: 'sky', files_completed: 'blue', pending_backlog: 'orange', avg_completion_time: 'purple', sla_compliance: 'green', productivity_change: 'cyan', active_processors: 'amber' };
const KpiCard = ({ cfg, kpi, definition, index = 0 }) => {
  const value = cfg.useLabel ? (kpi.label ?? '—')
    : kpi.current === null || kpi.current === undefined ? '—'
    : `${kpi.current}${cfg.suffix || ''}`;
  const prev = cfg.useLabel ? (kpi.prev_label ?? '—')
    : kpi.previous === null || kpi.previous === undefined ? '—'
    : `${kpi.previous}${cfg.suffix || ''}`;
  const chipPct = cfg.pts ? kpi.abs_change : kpi.change_pct;
  const numeric = !cfg.useLabel && typeof kpi.current === 'number' && !cfg.suffix;
  return (
    <AnKpiCard icon={cfg.icon} tone={KPI_TONE[cfg.key] || 'blue'} label={cfg.label} testid={`kpi-${cfg.key}`} delay={index * 50}
      value={numeric ? kpi.current : undefined} display={numeric ? undefined : value}
      delta={<AnDelta pct={chipPct ?? null} invert={cfg.invert} suffix={cfg.pts ? ' pts' : '%'} />}
      prev={`prev: ${prev}`}
      info={
        <UITooltip>
          <TooltipTrigger asChild>
            <span className="cursor-help text-slate-300 hover:text-slate-500" data-testid={`kpi-info-${cfg.key}`}><Info className="w-3.5 h-3.5" /></span>
          </TooltipTrigger>
          <TooltipContent side="bottom" className="max-w-[260px] text-xs">{definition}</TooltipContent>
        </UITooltip>
      } />
  );
};

const BarList = ({ rows, nameKey, testIdPrefix, onRowClick }) => {
  const max = Math.max(1, ...rows.map(r => r.count));
  return (
    <div className="space-y-2">
      {rows.map(r => (
        <div key={r[nameKey]}
          className={`flex items-center gap-2 rounded-md px-1 -mx-1 ${onRowClick ? 'cursor-pointer hover:bg-slate-50 focus-visible:ring-2 focus-visible:ring-blue-400 outline-none' : ''}`}
          onClick={onRowClick ? () => onRowClick(r[nameKey]) : undefined}
          role={onRowClick ? 'button' : undefined}
          tabIndex={onRowClick ? 0 : undefined}
          onKeyDown={onRowClick ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onRowClick(r[nameKey]); } } : undefined}
          data-testid={`${testIdPrefix}-${r[nameKey]}`}>
          <span className="text-xs text-slate-600 w-36 truncate">{r[nameKey]}</span>
          <div className="flex-1 h-2 rounded-full bg-slate-100 overflow-hidden">
            <div className="h-full rounded-full bg-orange-400" style={{ width: `${(r.count / max) * 100}%` }} />
          </div>
          <span className="text-xs font-semibold text-slate-700 w-8 text-right">{r.count}</span>
        </div>
      ))}
      {!rows.length && <p className="text-center text-slate-400 text-xs py-3">Nothing pending</p>}
    </div>
  );
};

const DashboardSkeleton = () => (
  <div className="space-y-5" data-testid="hist-skeleton">
    <div className="grid grid-cols-2 md:grid-cols-4 2xl:grid-cols-7 gap-4">
      {Array.from({ length: 7 }).map((_, i) => <Skeleton key={i} className="h-[118px] rounded-xl" />)}
    </div>
    <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-4 gap-4">
      {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-[290px] rounded-xl" />)}
    </div>
    <Skeleton className="h-[320px] rounded-xl" />
  </div>
);

const toneStyles = {
  positive: 'bg-emerald-50 border-emerald-200 text-emerald-900',
  warning: 'bg-amber-50 border-amber-200 text-amber-900',
  negative: 'bg-red-50 border-red-200 text-red-900',
  info: 'bg-blue-50 border-blue-200 text-blue-900',
};
const toneIcon = { positive: TrendingUp, warning: AlertTriangle, negative: Flame, info: Info };

export const HistoricalDashboard = ({ isAdmin, canExport, teamOrderType, workflowOptions = null, onWorkflowChange, users = [] }) => {
  const navigate = useNavigate();
  const initFilters = () => {
    const sp = new URLSearchParams(window.location.search);
    return {
      start: sp.get('start_date') || mtdStart(),
      end: sp.get('end_date') || today(),
      orderType: sp.get('order_type') || teamOrderType || 'all',
      product: sp.get('product') || 'all',
      client: sp.get('client') || 'all',
      userId: sp.get('user_id') || 'all',
    };
  };
  const [pending, setPending] = useState(initFilters);
  // Workflow change → re-scope Team Member list (and clear a member from the previous workflow)
  const setWorkflow = (v) => { setPending(p => ({ ...p, orderType: v, userId: 'all' })); onWorkflowChange?.(v); };
  const [applied, setApplied] = useState(pending);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [procPage, setProcPage] = useState(0);
  const [procSort, setProcSort] = useState({ key: 'files', dir: -1 });
  const abortRef = useRef(null);

  const fetchData = useCallback(async (f) => {
    abortRef.current?.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/productivity-analytics/historical-dashboard?${buildFilterParams(f)}`, { signal: ctrl.signal });
      if (ctrl.signal.aborted) return;
      setData(res.data);
      setApplied(f);
      setFailed(false);
      setProcPage(0);
      window.history.replaceState(null, '', `${window.location.pathname}?${buildFilterParams(f)}`);
    } catch (e) {
      if (axios.isCancel(e) || e.code === 'ERR_CANCELED') return;
      console.error('Historical dashboard fetch failed:', e);
      setFailed(true);
      toast.error('Failed to load analytics — showing previous results. Please try again.');
    } finally {
      if (abortRef.current === ctrl) setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(applied); return () => abortRef.current?.abort(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Filters are the single source of truth: any change auto-runs the report (debounced).
  const skipAutoRun = useRef(true);
  useEffect(() => {
    if (skipAutoRun.current) { skipAutoRun.current = false; return; }
    const t = setTimeout(() => fetchData(pending), 400);
    return () => clearTimeout(t);
  }, [pending, fetchData]);

  const runReport = () => fetchData(pending);

  const doExport = async () => {
    setExporting(true);
    try {
      const res = await axios.get(`${API}/api/productivity-analytics/historical-dashboard/export?${buildFilterParams(applied)}`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `historical_analytics_${applied.start}_${applied.end}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error('Export failed — please try again'); }
    setExporting(false);
  };

  const viewAll = (view, overrides = {}) => {
    const f = { ...applied, ...overrides };
    const p = new URLSearchParams(buildFilterParams(f));
    if (overrides.bucket) p.set('bucket', overrides.bucket);
    if (overrides.state) p.set('state', overrides.state);
    if (overrides.minAge) p.set('min_age', overrides.minAge);
    navigate(`/analytics/view-all?view=${view}&${p.toString()}`);
  };

  const k = data?.kpis;
  const defs = data?.metric_definitions || {};
  const products = data?.product_metrics || [];
  const clients = data?.client_metrics || [];
  const sla = data?.sla_metrics;
  const backlog = data?.backlog_metrics;
  const timeByProduct = useMemo(
    () => products.filter(p => p.completed > 0).map(p => ({ ...p, avg_days: +(p.avg_ms / 86400000).toFixed(2) })),
    [products]);
  const clientChart = useMemo(() => clients.slice(0, 6), [clients]);
  const slaPie = sla ? [
    { name: 'Within SLA', value: sla.within.count, pct: sla.within.pct, key: 'within' },
    { name: 'Near SLA', value: sla.near.count, pct: sla.near.pct, key: 'near' },
    { name: 'Over SLA', value: sla.over.count, pct: sla.over.pct, key: 'over' },
  ].filter(s => s.value > 0) : [];

  const sortedProcs = useMemo(() => {
    const arr = [...(data?.processor_metrics || [])];
    arr.sort((a, b) => {
      const av = a[procSort.key] ?? -1, bv = b[procSort.key] ?? -1;
      if (av === bv) return (b.files ?? 0) - (a.files ?? 0);
      return procSort.dir * (av > bv ? 1 : -1);
    });
    return arr;
  }, [data, procSort]);
  const procRows = sortedProcs.slice(procPage * 10, procPage * 10 + 10);
  const procPages = Math.ceil((sortedProcs.length || 0) / 10);
  const maxFiles = Math.max(1, ...sortedProcs.map(p => p.files));
  const toggleSort = (key) => setProcSort(s => ({ key, dir: s.key === key ? -s.dir : -1 }));

  return (
    <TooltipProvider delayDuration={150}>
      <div className="space-y-5" data-testid="historical-dashboard">
        {/* Filter bar — same pending/applied model; Run Report applies. Date Range = combined From/To (same ISO values). */}
        <AnFilterBar testId="hist-filter-bar" actions={
          <>
            <AnResetButton onClick={() => setPending(initFilters)} testId="hist-reset" />
            <AnRunButton onClick={runReport} loading={loading} testId="hist-run-report" />
            {canExport && (
              <Button variant="outline" onClick={doExport} disabled={exporting || loading} className="an-btn h-11 px-4 rounded-xl text-[13px] font-semibold" data-testid="hist-export">
                {exporting ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Download className="w-4 h-4 mr-1.5" />}Export
              </Button>
            )}
          </>
        }>
          <AnDateRange start={pending.start} end={pending.end} max={today()} testId="hist-date-range"
            onChange={({ start, end }) => setPending(p => ({ ...p, start, end }))} />
          <input type="hidden" value={pending.start} data-testid="hist-start-date" readOnly />
          <input type="hidden" value={pending.end} data-testid="hist-end-date" readOnly />
          {workflowOptions && (
            <AnFilterField icon={FileCheck2} label="Workflow" className="min-w-[150px]">
              <Select value={pending.orderType} onValueChange={setWorkflow}>
                <SelectTrigger data-testid="hist-order-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {workflowOptions.map(o => <SelectItem key={o.value} value={o.value} data-testid={`hist-workflow-${o.value}`}>{o.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </AnFilterField>
          )}
          <AnFilterField icon={Layers} label="Product" className="min-w-[170px]">
            <Select value={pending.product} onValueChange={v => setPending(p => ({ ...p, product: v }))}>
              <SelectTrigger data-testid="hist-product"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Products</SelectItem>
                {(data?.product_options || []).map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}
              </SelectContent>
            </Select>
          </AnFilterField>
          <AnFilterField icon={Inbox} label="Client" className="min-w-[170px]">
            <Select value={pending.client} onValueChange={v => setPending(p => ({ ...p, client: v }))}>
              <SelectTrigger data-testid="hist-client"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Clients</SelectItem>
                {(data?.client_options || []).map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
          </AnFilterField>
          {isAdmin && (
            <AnFilterField icon={Users} label="Team Member" className="min-w-[190px]">
              <TeamMemberSelect value={pending.userId} onChange={v => setPending(p => ({ ...p, userId: v }))} users={users} testId="hist-user" />
            </AnFilterField>
          )}
          <span className="text-[11px] text-slate-400 self-center pl-1" data-testid="hist-range-label">
            {applied.start} → {applied.end} (vs {data?.comparison_period?.start} → {data?.comparison_period?.end})
          </span>
        </AnFilterBar>

        {loading && !data ? (
          <DashboardSkeleton />
        ) : !data ? (
          <div className="text-center py-24" data-testid="hist-error">
            <p className="text-slate-500 mb-3">Failed to load analytics{failed ? ' — please retry' : ''}.</p>
            <Button variant="outline" onClick={() => fetchData(applied)} data-testid="hist-retry">Retry</Button>
          </div>
        ) : (
          <>
            {data.workflow_stage === 'typing' && (
              <div className="flex items-start gap-2 rounded-xl border border-indigo-200 bg-indigo-50/70 px-4 py-2.5 text-[12px] text-indigo-900" data-testid="hist-typing-stage-note">
                <Info className="w-4 h-4 mt-0.5 shrink-0 text-indigo-600" />
                <span><span className="font-semibold">Typing stage credit.</span> Files Completed counts Typing-stage completions credited to Typing Processors. The same order also carries one Search credit under Title Search — stage credits are not added into the All / company order totals.</span>
              </div>
            )}
            {/* Executive KPI grid — 5 + 4 (choice 1a) */}
            <div className="grid grid-cols-2 md:grid-cols-4 2xl:grid-cols-7 gap-4" data-testid="hist-kpis">
              {KPI_CONFIG.map((cfg, i) => <KpiCard key={cfg.key} cfg={cfg} kpi={k[cfg.key]} definition={defs[cfg.key]} index={i} />)}
            </div>

            {/* Charts row */}
            <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-4 gap-4">
              <SectionCard title="Completion by Product" subtitle="Completed files per product" testId="hist-by-product">
                {timeByProduct.length ? (
                  <ResponsiveContainer width="100%" height={Math.max(170, Math.min(230, timeByProduct.length * 52))}>
                    <BarChart data={timeByProduct.slice(0, 6)} layout="vertical" margin={{ left: 4, right: 56, top: 2, bottom: 2 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" horizontal={false} />
                      <XAxis type="number" tick={{ fontSize: 10 }} allowDecimals={false} />
                      <YAxis type="category" dataKey="product" width={104} tick={{ fontSize: 10 }} />
                      <RTooltip formatter={(v, n, p) => [`${v} (${p.payload.percent}%)`, 'Completed']} />
                      <Bar dataKey="completed" fill="#2563EB" radius={[0, 4, 4, 0]} barSize={timeByProduct.length <= 3 ? 28 : 18}
                        onClick={(d) => viewAll('sla', { product: d.product })} cursor="pointer">
                        <LabelList dataKey="completed" position="right" formatter={(v) => v} style={{ fontSize: 10, fill: '#334155', fontWeight: 600 }} />
                        <LabelList dataKey="percent" position="right" dx={20} formatter={(v) => `${v}%`} style={{ fontSize: 9, fill: '#94A3B8' }} />
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                ) : <EmptyState />}
              </SectionCard>

              <SectionCard title="Client Volumes" subtitle="Received vs completed (top clients)" testId="hist-client-volumes"
                action={<button onClick={() => viewAll('clients')} className="text-xs font-medium text-blue-600 hover:text-blue-700 inline-flex items-center gap-0.5" data-testid="hist-view-all-clients">View all <ArrowUpRight className="w-3 h-3" /></button>}>
                {clientChart.length ? (
                  <ResponsiveContainer width="100%" height={Math.max(180, Math.min(240, clientChart.length * 46))}>
                    <BarChart data={clientChart} layout="vertical" margin={{ left: 4, right: 36, top: 2, bottom: 2 }} barGap={1}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" horizontal={false} />
                      <XAxis type="number" tick={{ fontSize: 10 }} allowDecimals={false} />
                      <YAxis type="category" dataKey="client" width={104} tick={{ fontSize: 10 }} />
                      <RTooltip formatter={(v, n) => [v, n]} />
                      <Legend wrapperStyle={{ fontSize: 11 }} iconSize={9} />
                      <Bar name="Received" dataKey="received" fill="#93C5FD" radius={[0, 3, 3, 0]} barSize={clientChart.length <= 3 ? 14 : 10}
                        onClick={(d) => viewAll('backlog', { client: d.client })} cursor="pointer">
                        <LabelList dataKey="received" position="right" style={{ fontSize: 9, fill: '#64748B' }} />
                      </Bar>
                      <Bar name="Completed" dataKey="completed" fill="#2563EB" radius={[0, 3, 3, 0]} barSize={clientChart.length <= 3 ? 14 : 10}
                        onClick={(d) => viewAll('backlog', { client: d.client })} cursor="pointer">
                        <LabelList dataKey="completed" position="right" style={{ fontSize: 9, fill: '#1E40AF', fontWeight: 600 }} />
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                ) : <EmptyState />}
              </SectionCard>

              <SectionCard title="Avg Completion Time by Product" subtitle="In days" testId="hist-time-by-product">
                {timeByProduct.length ? (
                  <ResponsiveContainer width="100%" height={Math.max(170, Math.min(230, timeByProduct.length * 52))}>
                    <BarChart data={timeByProduct.slice(0, 6)} layout="vertical" margin={{ left: 4, right: 48, top: 2, bottom: 2 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" horizontal={false} />
                      <XAxis type="number" tick={{ fontSize: 10 }} />
                      <YAxis type="category" dataKey="product" width={104} tick={{ fontSize: 10 }} />
                      <RTooltip formatter={(v, n, p) => [p.payload.avg_label, 'Avg time']} />
                      <Bar dataKey="avg_days" fill="#8B5CF6" radius={[0, 4, 4, 0]} barSize={timeByProduct.length <= 3 ? 28 : 18}
                        onClick={(d) => viewAll('sla', { product: d.product })} cursor="pointer">
                        <LabelList dataKey="avg_label" position="right" style={{ fontSize: 10, fill: '#334155' }} />
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                ) : <EmptyState />}
              </SectionCard>

              <SectionCard title="SLA Performance" subtitle="Click a segment to see the files" testId="hist-sla">
                {slaPie.length ? (
                  <>
                    <div className="relative">
                      <ResponsiveContainer width="100%" height={172}>
                        <PieChart>
                          <Pie data={slaPie} dataKey="value" nameKey="name" innerRadius={56} outerRadius={82} paddingAngle={2}
                            onClick={(d) => viewAll('sla', { bucket: d.key })} cursor="pointer">
                            {slaPie.map(s => <Cell key={s.key} fill={SLA_COLORS[s.key]} />)}
                          </Pie>
                          <RTooltip formatter={(v, n, p) => [`${v} files (${p.payload.pct}%)`, n]} />
                        </PieChart>
                      </ResponsiveContainer>
                      <div className="absolute inset-x-0 top-0 h-[172px] flex flex-col items-center justify-center pointer-events-none">
                        <span className="text-xl font-bold text-slate-900">{sla.compliance_pct != null ? `${sla.compliance_pct}%` : '—'}</span>
                        <span className="text-[10px] text-slate-400 uppercase tracking-wide">Compliant</span>
                      </div>
                    </div>
                    <div className="flex justify-center gap-3 mt-1 text-[11px]">
                      {['within', 'near', 'over'].map(key => (
                        <button key={key} onClick={() => viewAll('sla', { bucket: key })}
                          className="inline-flex items-center gap-1 text-slate-600 hover:text-slate-900" data-testid={`sla-legend-${key}`}>
                          <span className="w-2 h-2 rounded-full" style={{ background: SLA_COLORS[key] }} />
                          {key === 'within' ? 'Within' : key === 'near' ? 'Near' : 'Over'} {sla[key].count} ({sla[key].pct}%)
                        </button>
                      ))}
                    </div>
                  </>
                ) : <EmptyState label="No completed files in this period" />}
              </SectionCard>
            </div>

            {/* Detail panels */}
            <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
              <div className="xl:col-span-2">
                <SectionCard title="Processor Productivity Comparison" subtitle="Click a column to sort"
                  testId="hist-processors"
                  action={<button onClick={() => viewAll('processors')} className="text-xs font-medium text-blue-600 hover:text-blue-700 inline-flex items-center gap-0.5" data-testid="hist-view-all-processors">View all processors <ArrowUpRight className="w-3 h-3" /></button>}>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm min-w-[640px]">
                      <thead>
                        <tr className="an-thead text-left text-[11px] uppercase tracking-wide">
                          {[['name', 'Processor'], ['files', 'Files'], ['avg_ms', 'Avg Time'], ['sla_pct', 'SLA %'], ['daily_avg', 'Daily Avg'], ['assigned', 'Assigned'], ['completion_rate', 'Compl. %'], ['change_pct', 'vs Prev']].map(([key, label]) => (
                            <th key={key} className="py-2 pr-3 cursor-pointer select-none whitespace-nowrap" onClick={() => toggleSort(key)} data-testid={`hist-proc-sort-${key}`}>
                              {label}{procSort.key === key ? (procSort.dir === -1 ? ' ↓' : ' ↑') : ''}
                            </th>
                          ))}
                          <th className="py-2">Trend</th>
                        </tr>
                      </thead>
                      <tbody>
                        {procRows.map(p => (
                          <tr key={p.user_id} className="border-b border-slate-50 hover:bg-slate-50/60">
                            <td className="py-2 pr-3 font-medium text-slate-800 whitespace-nowrap">{p.name}</td>
                            <td className="py-2 pr-3">
                              <div className="flex items-center gap-2">
                                <span className="text-slate-800 font-semibold w-6">{p.files}</span>
                                <div className="w-14 h-1.5 rounded-full bg-slate-100 overflow-hidden">
                                  <div className="h-full rounded-full bg-blue-500" style={{ width: `${(p.files / maxFiles) * 100}%` }} />
                                </div>
                              </div>
                            </td>
                            <td className="py-2 pr-3 text-slate-700 whitespace-nowrap">{p.avg_label}</td>
                            <td className="py-2 pr-3">
                              <span className={`font-medium ${p.sla_pct >= 95 ? 'text-emerald-600' : p.sla_pct >= 85 ? 'text-amber-600' : 'text-red-500'}`}>{p.sla_pct}%</span>
                            </td>
                            <td className="py-2 pr-3 text-slate-700">{p.daily_avg}</td>
                            <td className="py-2 pr-3 text-slate-700">{p.assigned}</td>
                            <td className="py-2 pr-3 text-slate-700">{p.completion_rate != null ? `${p.completion_rate}%` : '—'}</td>
                            <td className="py-2 pr-3"><DeltaChip pct={p.change_pct} /></td>
                            <td className="py-1"><Sparkline data={p.trend} /></td>
                          </tr>
                        ))}
                        {!procRows.length && <tr><td colSpan={9} className="py-6 text-center text-slate-400">No completions in this period</td></tr>}
                      </tbody>
                    </table>
                  </div>
                  {procPages > 1 && (
                    <div className="flex items-center justify-end gap-2 mt-3 text-xs text-slate-500">
                      <button disabled={procPage === 0} onClick={() => setProcPage(p => p - 1)} className="px-2 py-1 rounded border border-slate-200 disabled:opacity-40" data-testid="hist-proc-prev">Prev</button>
                      <span data-testid="hist-proc-page">{procPage + 1} / {procPages}</span>
                      <button disabled={procPage >= procPages - 1} onClick={() => setProcPage(p => p + 1)} className="px-2 py-1 rounded border border-slate-200 disabled:opacity-40" data-testid="hist-proc-next">Next</button>
                    </div>
                  )}
                </SectionCard>
              </div>

              <SectionCard title="Top Performers" subtitle="Balanced score: volume · SLA · speed" testId="hist-top-performers"
                action={<button onClick={() => viewAll('performers')} className="text-xs font-medium text-blue-600 hover:text-blue-700 inline-flex items-center gap-0.5" data-testid="hist-view-all-performers">View all <ArrowUpRight className="w-3 h-3" /></button>}>
                <div className="space-y-3">
                  {(data.top_performers || []).map((p) => (
                    <UITooltip key={p.user_id}>
                      <TooltipTrigger asChild>
                        <div className="flex items-center gap-3 cursor-help" data-testid={`top-performer-${p.rank}`}>
                          <span className={`w-6 h-6 rounded-full flex items-center justify-center text-[11px] font-bold shrink-0 ${p.rank === 1 ? 'bg-amber-100 text-amber-700' : p.rank === 2 ? 'bg-slate-200 text-slate-600' : p.rank === 3 ? 'bg-orange-100 text-orange-700' : 'bg-slate-100 text-slate-500'}`}>{p.rank}</span>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <p className="text-sm font-medium text-slate-800 truncate">{p.name}</p>
                              <DeltaChip pct={p.change_pct} />
                            </div>
                            <p className="text-[11px] text-slate-400 truncate">{p.files} files · {p.avg_label} avg · {p.sla_pct}% SLA</p>
                          </div>
                          <span className="text-xs font-bold text-white bg-slate-900 rounded-md px-1.5 py-0.5 shrink-0">{p.score}</span>
                        </div>
                      </TooltipTrigger>
                      <TooltipContent side="left" className="max-w-[280px] text-xs">{p.reason}</TooltipContent>
                    </UITooltip>
                  ))}
                  {!(data.top_performers || []).length && <p className="text-center text-slate-400 text-sm py-4">No data</p>}
                </div>
              </SectionCard>
            </div>

            {/* Backlog & Bottlenecks (choice 3a) */}
            <SectionCard title="Backlog & Bottlenecks" subtitle={`Pending workload as of ${applied.end}`} testId="hist-backlog"
              action={<button onClick={() => viewAll('backlog')} className="text-xs font-medium text-blue-600 hover:text-blue-700 inline-flex items-center gap-0.5" data-testid="hist-view-backlog">View backlog details <ArrowUpRight className="w-3 h-3" /></button>}>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
                {[
                  { label: 'Total Pending', value: backlog.total, icon: Layers, tint: 'text-slate-700 bg-slate-100', testId: 'backlog-total', click: () => viewAll('backlog') },
                  { label: 'Oldest Active File', value: backlog.oldest?.order_number || '—', sub: backlog.oldest?.age_days != null ? `${backlog.oldest.age_days} days` : null, icon: Timer, tint: 'text-blue-700 bg-blue-50', testId: 'backlog-oldest', click: () => viewAll('backlog'), small: true },
                  { label: 'Avg Pending Age (excl. >1yr)', value: backlog.avg_age_active_label, icon: Hourglass, tint: 'text-violet-700 bg-violet-50', testId: 'backlog-avg-age' },
                  { label: 'Pending >30 Days', value: backlog.aging?.over_30, icon: CalendarClock, tint: 'text-sky-700 bg-sky-50', testId: 'backlog-over30', click: () => viewAll('backlog', { minAge: 30 }) },
                  { label: 'Pending >60 Days', value: backlog.aging?.over_60, icon: CalendarClock, tint: 'text-orange-700 bg-orange-50', testId: 'backlog-over60', click: () => viewAll('backlog', { minAge: 60 }) },
                  { label: 'Pending >90 Days', value: backlog.aging?.over_90, icon: CalendarClock, tint: 'text-rose-700 bg-rose-50', testId: 'backlog-over90', click: () => viewAll('backlog', { minAge: 90 }) },
                  { label: 'Approaching SLA', value: backlog.approaching_sla, icon: AlertTriangle, tint: 'text-amber-700 bg-amber-50', testId: 'backlog-approaching', click: () => viewAll('backlog', { state: 'approaching' }) },
                  { label: 'Over SLA', value: backlog.over_sla, icon: Flame, tint: 'text-red-700 bg-red-50', testId: 'backlog-over', click: () => viewAll('backlog', { state: 'over' }) },
                ].map(t => (
                  <div key={t.label} onClick={t.click}
                    role={t.click ? 'button' : undefined}
                    tabIndex={t.click ? 0 : undefined}
                    onKeyDown={t.click ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); t.click(); } } : undefined}
                    className={`rounded-lg border border-slate-100 p-3 flex items-center gap-3 ${t.click ? 'cursor-pointer hover:border-slate-300 transition-colors focus-visible:ring-2 focus-visible:ring-blue-400 outline-none' : ''}`}
                    data-testid={t.testId}>
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${t.tint}`}><t.icon className="w-4 h-4" /></div>
                    <div className="min-w-0">
                      <p className={`${t.small ? 'text-sm' : 'text-lg'} font-bold text-slate-900 leading-tight truncate`}>{t.value ?? '—'}{t.sub && <span className="text-[11px] font-semibold text-slate-500 ml-1.5">({t.sub})</span>}</p>
                      <p className="text-[11px] text-slate-500 truncate">{t.label}</p>
                    </div>
                  </div>
                ))}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Backlog by Product <span className="normal-case font-normal text-slate-400">— click to open files</span></p>
                  <BarList rows={backlog.by_product} nameKey="product" testIdPrefix="backlog-prod"
                    onRowClick={(name) => viewAll('backlog', { product: name })} />
                </div>
                <div>
                  <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Backlog by Client <span className="normal-case font-normal text-slate-400">— click to open files</span></p>
                  <BarList rows={backlog.by_client} nameKey="client" testIdPrefix="backlog-client"
                    onRowClick={(name) => viewAll('backlog', { client: name })} />
                </div>
              </div>
            </SectionCard>

            {/* Insights */}
            <SectionCard title="Key Insights & Recommended Actions" subtitle={`Compared with ${data.comparison_period.start} → ${data.comparison_period.end}`} testId="hist-insights">
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3">
                {(data.insights || []).map((ins, i) => {
                  const Icon = toneIcon[ins.tone] || Lightbulb;
                  const sevDot = ins.severity === 'critical' ? 'bg-red-500' : ins.severity === 'warning' ? 'bg-orange-400' : 'bg-emerald-500';
                  const sevLabel = ins.severity === 'critical' ? 'Critical' : ins.severity === 'warning' ? 'Warning' : 'Info';
                  return (
                    <div key={i} className={`rounded-lg border p-3.5 ${toneStyles[ins.tone] || toneStyles.info}`} data-testid={`insight-${ins.kind}`}>
                      <div className="flex items-center gap-1.5 mb-1.5">
                        <span className={`w-2 h-2 rounded-full shrink-0 ${sevDot}`} title={sevLabel} data-testid={`insight-severity-${ins.severity}`} />
                        <Icon className="w-3.5 h-3.5 shrink-0" /><span className="text-xs font-bold">{ins.title}</span>
                      </div>
                      <p className="text-[11px] leading-relaxed">{ins.what_changed}</p>
                      {ins.why && <p className="text-[11px] leading-relaxed opacity-80 mt-1"><span className="font-semibold">Why:</span> {ins.why}</p>}
                      {ins.impact && <p className="text-[11px] leading-relaxed opacity-75 mt-1 italic">{ins.impact}</p>}
                      {ins.action && (
                        <p className="text-[11px] leading-relaxed mt-1.5">
                          <span className="font-semibold">Recommended action:</span> {ins.action}
                        </p>
                      )}
                    </div>
                  );
                })}
                {!(data.insights || []).length && <p className="text-slate-400 text-sm col-span-full text-center py-2">No significant changes in this period</p>}
              </div>
            </SectionCard>
          </>
        )}
      </div>
    </TooltipProvider>
  );
};

export default HistoricalDashboard;
